import { useRouter, usePathname, useSearchParams } from "next/navigation";
import {
	createContext,
	Dispatch,
	FC,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from "react";
import { useAppContext } from "context/AppContext";
import mixpanel from "mixpanel-browser";
import moment from "moment";

import marketingLinkRewrites from "config/marketing-links.json";
import partnerLinkRewrites from "config/partner-pages.json";
import realtorReferalPartners from "config/realtor-referral-partners.json";

export interface IOfferContext {
	offer: string | undefined;
	setOffer: Dispatch<SetStateAction<string | undefined>>;
	offerSetAt: string | undefined;
	setOfferSetAt: Dispatch<SetStateAction<string | undefined>>;
	partnerRef: string | undefined;
	showOfferModal: boolean;
	setShowOfferModal: Dispatch<SetStateAction<boolean>>;
	offerContents: { title: string; subtitle: string } | undefined;
	contingency: number | undefined;
	setContingency: Dispatch<SetStateAction<number | undefined>>;
}

export const OfferContext = createContext<IOfferContext>({
	offer: undefined,
	setOffer: () => {},
	offerSetAt: undefined,
	setOfferSetAt: () => {},
	partnerRef: undefined,
	showOfferModal: false,
	setShowOfferModal: () => {},
	offerContents: undefined,
	contingency: undefined,
	setContingency: () => {},
});

interface PersistedOffer {
	offer: string;
	set_at: string;
	is_partner: boolean;
}

const ALL_MARKETING_URLS = marketingLinkRewrites.map(({ source }) => source);

const LEGACY_PARTNER_PAGE_URLS = Object.values(partnerLinkRewrites).reduce<
	string[]
>((acc, config) => [...acc, ...config.urls], []);

const LEGACY_REALTOR_PARTNER_URLS = Object.values(
	realtorReferalPartners
).reduce<string[]>((acc, config) => [...acc, ...config.urls], []);

const getCampaignFromUrl = (): {
	campaign: string;
	is_partner: boolean;
} | null => {
	if (typeof window === "undefined") return null;

	const url = new URL(window.location.href);
	const params = url.searchParams;

	if (ALL_MARKETING_URLS.includes(url.pathname)) {
		return { campaign: url.pathname.slice(1), is_partner: false };
	}

	if (LEGACY_PARTNER_PAGE_URLS.includes(url.pathname.slice(1))) {
		return { campaign: url.pathname.slice(1), is_partner: true };
	}

	if (LEGACY_REALTOR_PARTNER_URLS.includes(url.pathname.slice(1))) {
		return { campaign: url.pathname.slice(1), is_partner: true };
	}

	if (url.pathname.startsWith("/partner/")) {
		return {
			campaign: url.pathname.replace(/^\/partner\//g, ""),
			is_partner: true,
		};
	}
	if (url.pathname.startsWith("/partners/")) {
		return {
			campaign: url.pathname.replace(/^\/partners\//g, ""),
			is_partner: true,
		};
	}

	const partner_ref_param = params.get("partner_ref");

	if (partner_ref_param) {
		return { campaign: partner_ref_param, is_partner: true };
	}

	const utm_campaign_param = params.get("utm_campaign");

	if (utm_campaign_param) {
		return { campaign: utm_campaign_param, is_partner: false };
	}

	if (url.pathname.startsWith("/lp")) {
		return { campaign: `lp_${url.pathname.slice(4)}`, is_partner: false };
	}

	return null;
};

const OFFER_LOCAL_STORAGE_KEY = "offer";
const PARTNER_REF_LOCAL_STORAGE_KEY__DEPRECATED = "partner_ref";

export const OfferContextProvider: FC = ({ children }) => {
	const [offer, setOffer] = useState<string>();
	const [offerSetAt, setOfferSetAt] = useState<string>();
	const [offerContents, setOfferContents] = useState<{
		title: string;
		subtitle: string;
	}>();
	const [showOfferModal, setShowOfferModal] = useState(false);
	const [contingency, setContingency] = useState<number>();
	const [partnerRef, setPartnerRef] = useState<string>();

	const searchParams = useSearchParams();
	const pathname = usePathname();

	const { mixpanelInitialized } = useAppContext();

	useEffect(() => {
		// Set state from local storage
		try {
			const persisted_offer_raw = window.localStorage.getItem(
				OFFER_LOCAL_STORAGE_KEY
			);

			// Support backwards compatibility with PARTNER_REF_LOCAL_STORAGE_KEY
			// This can be deleted after a couple months of deprecation - 5/21
			const persisted_offer: PersistedOffer | null = persisted_offer_raw
				? JSON.parse(persisted_offer_raw)
				: null;

			const persisted_partner_ref_raw = window.localStorage.getItem(
				PARTNER_REF_LOCAL_STORAGE_KEY__DEPRECATED
			);

			const persisted_partner_ref = persisted_partner_ref_raw
				? persisted_offer_raw //
				: null;

			if (persisted_offer) {
				setOffer(persisted_offer.offer);
				setOfferSetAt(persisted_offer.set_at);
				if (persisted_offer.is_partner) {
					setPartnerRef(persisted_offer.offer);
				}
			}

			// Support backwards compatibility with PARTNER_REF_LOCAL_STORAGE_KEY
			if (persisted_partner_ref) {
				setPartnerRef(persisted_partner_ref);
			}
		} catch {}

		// Set state from URL
		const campaignFromUrl = getCampaignFromUrl();

		if (campaignFromUrl) {
			const { campaign, is_partner } = campaignFromUrl;

			setOffer(campaign);
			setOfferSetAt(moment().toISOString());
			if (is_partner) {
				setPartnerRef(campaign);
			}

			try {
				const offerBody: PersistedOffer = {
					offer: campaign,
					set_at: moment().toISOString(),
					is_partner,
				};

				window.localStorage.setItem(
					OFFER_LOCAL_STORAGE_KEY,
					JSON.stringify(offerBody)
				);
			} catch {}
		}
	}, [pathname, searchParams]);

	useEffect(() => {
		switch (offer) {
			// case "FebPost_Offer":
			// 	setOfferContents({
			// 		title: "Your discount has applied!",
			// 		subtitle:
			// 			"Remember to complete your sign up to receive the discount.",
			// 	});
			// 	break;
			// case "save":
			// 	setOfferContents({
			// 		title: "Your discount has applied!",
			// 		subtitle:
			// 			"Remember to complete your sign up by March 31 to receive your free Yeti tumbler. New customers only.",
			// 	});

			// 	break;
			// case "altgage":
			case "specialized247":
				// case "vedulagroup":
				setOfferContents({
					title: "Your discount has applied!",
					subtitle:
						"Any properties that you sign up during this session will receive a discounted rate of 20% instead of our typical 25% contingency!",
				});

				break;
		}
	}, [offer]);

	useEffect(() => {
		if (offer && mixpanelInitialized) {
			mixpanel.register({
				campaign: offer,
			});
		}
	}, [offer, mixpanelInitialized]);

	useEffect(() => {
		if (offer && offerContents) {
			setShowOfferModal(true);
		}
	}, [offer, offerContents]);

	return (
		<OfferContext.Provider
			value={{
				offer,
				setOffer,
				offerSetAt,
				setOfferSetAt,
				partnerRef,
				contingency,
				setContingency,
				showOfferModal,
				setShowOfferModal,
				offerContents,
			}}>
			{children}
		</OfferContext.Provider>
	);
};

export const useOfferContext = () => useContext(OfferContext);
